import React, { useMemo, useState } from 'react'
import {
  PAGE_SIZE, DataTable, Pagination, Pill, ViewButton,
  groupDocuments, groupTraining, groupCertifications,
  assignmentStatusClass, assignmentStatusLabel, verificationClass, expiryClass
} from './complianceShared'
import RecordDetail from './RecordDetail.js'

const HEADERS = {
  documents: [
    { label: 'Sr. No.' }, { label: 'Employee ID' }, { label: 'Employee Name' },
    { label: 'Documents', center: true }, { label: 'Document Types' }, { label: 'Action', center: true }
  ],
  training: [
    { label: 'Sr. No.' }, { label: 'Employee ID' }, { label: 'Employee Name' },
    { label: 'Assessments', center: true }, { label: 'Status' }, { label: 'Completion' },
    { label: 'Overdue', center: true }, { label: 'Action', center: true }
  ],
  certification: [
    { label: 'Sr. No.' }, { label: 'Employee ID' }, { label: 'Employee Name' },
    { label: 'Certificates', center: true }, { label: 'Verification' }, { label: 'Expiry' },
    { label: 'Action', center: true }
  ]
}

const EMPTY = {
  documents: ['Loading employee documents…', 'No employee documents on the current submitted forms.'],
  training: ['Loading training…', 'No training assigned yet.'],
  certification: ['Loading certifications…', 'No certifications yet.']
}

const cell = 'py-4 px-4 border-r text-gray-900'

// One component for the three list tabs (type = documents | training | certification)
function RecordsTab({ type, employeeDocuments, trainingAssignments, credentialRecords, loading, onViewDocument }) {
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState(null)

  const rows = useMemo(() => {
    if (type === 'documents') return groupDocuments(employeeDocuments)
    if (type === 'training') return groupTraining(trainingAssignments)
    return groupCertifications(credentialRecords)
  }, [type, employeeDocuments, trainingAssignments, credentialRecords])

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const paged = rows.slice(startIndex, startIndex + PAGE_SIZE)

  const renderCells = (group) => {
    if (type === 'documents') {
      const titles = [...new Set(group.items.map((d) => d.title || d.originalName || d.fileName || 'Untitled'))]
      return (
        <>
          <td className={`${cell} text-center`}>{group.items.length}</td>
          <td className='py-4 px-4 border-r'>{titles.slice(0, 2).join(', ')}{titles.length > 2 ? ` +${titles.length - 2} more` : ''}</td>
        </>
      )
    }
    if (type === 'training') {
      return (
        <>
          <td className={`${cell} text-center`}>{group.items.length}</td>
          <td className='py-4 px-4 border-r'>
            <div className='flex flex-wrap gap-1.5'>
              {['assigned', 'in_progress', 'completed'].filter((k) => group.counts[k] > 0).map((k) => (
                <Pill key={k} className={assignmentStatusClass(k)}>{group.counts[k]} {assignmentStatusLabel(k)}</Pill>
              ))}
            </div>
          </td>
          <td className='py-4 px-4 border-r'>
            <div className='flex items-center gap-2 min-w-[120px]'>
              <div className='h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden'>
                <div className='h-full bg-green-300' style={{ width: `${group.completion}%` }} />
              </div>
              <span className='text-xs text-gray-600'>{group.completion}%</span>
            </div>
          </td>
          <td className='py-4 px-4 border-r text-center'>
            {group.overdue > 0
              ? <Pill className='bg-red-50 text-red-500 border-red-100'>{group.overdue}</Pill>
              : <span className='text-gray-400'>0</span>}
          </td>
        </>
      )
    }
    return (
      <>
        <td className={`${cell} text-center`}>{group.items.length}</td>
        <td className='py-4 px-4 border-r'>
          <div className='flex flex-wrap gap-1.5'>
            {['pending', 'verified', 'rejected'].filter((k) => group.counts[k] > 0).map((k) => (
              <Pill key={k} className={`capitalize ${verificationClass(k)}`}>{group.counts[k]} {k}</Pill>
            ))}
          </div>
        </td>
        <td className='py-4 px-4 border-r'><Pill className={expiryClass(group.expiry)}>{group.expiry}</Pill></td>
      </>
    )
  }

  const tableRows = paged.map((group, index) => (
    <tr key={group.key} className='bg-white border-b hover:bg-gray-50'>
      <th scope='row' className='py-4 px-4 border-r font-medium text-gray-900'>{startIndex + index + 1}</th>
      <td className={cell}>{group.code || '—'}</td>
      <td className={cell}>{group.name || '—'}</td>
      {renderCells(group)}
      <td className='py-4 px-4 text-center'><ViewButton onClick={() => setDetail(group)} /></td>
    </tr>
  ))

  return (
    <div className='bg-white border rounded-lg border-slate-200 p-3'>
      <DataTable headers={HEADERS[type]} rows={tableRows} empty={EMPTY[type][loading ? 0 : 1]} />
      <Pagination page={currentPage} pageCount={pageCount} totalItems={rows.length} onChange={setPage} />
      {detail && (
        <RecordDetail type={type} group={detail} credentialRecords={credentialRecords} onClose={() => setDetail(null)} onViewDocument={onViewDocument} />
      )}
    </div>
  )
}

export default RecordsTab